import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { AlertTriangle, FileText, Mail, Phone } from 'lucide-react';
import './LegalPage.css';

const LAST_UPDATED = 'February 28, 2026';
const SITE_NAME    = 'Rajesh R – AI Platform Optimization Expert';
const SITE_URL     = 'https://keralas-1-ai-platform-optimization-expert.web.app';
const CONTACT_EMAIL = 'info@rajeshrnair.com';
const CONTACT_PHONE = '+91 79070 38984';
const WA_LINK = 'https://wa.me/917907038984?text=Hi%20Rajesh!%20I%20have%20a%20query%20about%20your%20Terms%20%26%20Conditions.';

export default function TermsConditions() {
  const sections = [
    {
      num: 1,
      title: 'Acceptance of Terms',
      content: (
        <>
          <p>
            By accessing or using any part of this website, including the Free Site Audit Tool,
            you agree to be bound by these Terms and Conditions. If you do not agree to all of
            these terms, please do not use this website or any of its tools.
          </p>
          <p>
            These terms apply to all visitors, users, and anyone who accesses or uses our
            services, tools, or content provided on this website.
          </p>
        </>
      ),
    },
    {
      num: 2,
      title: 'Free Site Audit Tool — Accuracy Disclaimer',
      highlight: true,
      content: (
        <>
          <p>
            The Free Site Audit Tool available on this website is provided <strong>for
            informational and educational purposes only</strong>. The audit results are
            generated automatically by analysing publicly accessible HTML content of the
            submitted URL and, where enabled, by the Google Gemini AI API (a third-party
            service).
          </p>
          <p><strong>Important limitations you must understand:</strong></p>
          <ul>
            <li>Audit results are <strong>not guaranteed to be 100% accurate, complete, or up to date</strong>. Automated tools cannot replicate a full human expert review.</li>
            <li>The tool analyses only the <strong>publicly accessible HTML</strong> of the entered URL at the time of the request. It does not crawl linked pages, sitemaps, or subpages.</li>
            <li>AI-generated insights (when Gemini API is used) are produced by a third-party AI model and may contain errors, omissions, or hallucinations.</li>
            <li>Scores and recommendations are <strong>estimates based on heuristics</strong>. They do not represent a professional audit or certified assessment.</li>
            <li>Results may differ each time depending on page load conditions, proxy availability, and AI model responses.</li>
            <li>The tool does not analyse JavaScript-rendered content, server-side logic, Core Web Vitals measured in real browsers, or any back-end systems.</li>
          </ul>
          <div className="legal-highlight-box">
            <p>
              <strong>For accurate, reliable, and actionable AI optimisation results,
              you must consult directly with Rajesh R.</strong> A professional audit
              involves manual review, real browser testing, competitor analysis, and
              a tailored strategy — none of which this free automated tool provides.
            </p>
          </div>
        </>
      ),
    },
    {
      num: 3,
      title: 'No Professional or Legal Advice',
      content: (
        <>
          <p>
            Nothing on this website, including audit reports, AI-generated recommendations,
            blog content, or any other material, constitutes professional SEO, legal,
            financial, or technical advice. All content is provided for general informational
            purposes only.
          </p>
          <p>
            You should not act on any information obtained from this website without first
            seeking independent professional advice appropriate to your specific situation.
            Rajesh R and this website accept no responsibility for any action taken based
            on information obtained from this site without direct consultation.
          </p>
        </>
      ),
    },
    {
      num: 4,
      title: 'Third-Party Services & APIs',
      content: (
        <>
          <p>
            The Free Site Audit Tool uses the following third-party services to function:
          </p>
          <ul>
            <li><strong>CORS Proxy Services</strong> (allorigins.win, corsproxy.io) — used to fetch publicly accessible website HTML. We do not control or endorse these services.</li>
            <li><strong>Google Gemini API</strong> — used when you provide your own API key to generate AI-powered insights. Your API key is stored only in your local browser and is never transmitted to our servers. Google's own terms and privacy policy apply to your use of their API.</li>
          </ul>
          <p>
            We are not responsible for the availability, accuracy, or terms of any
            third-party service. If a third-party service is unavailable or returns
            incorrect results, our tool may produce incomplete or inaccurate outputs.
          </p>
        </>
      ),
    },
    {
      num: 5,
      title: 'Limitation of Liability',
      content: (
        <>
          <p>
            To the fullest extent permitted by applicable law, Rajesh R, this website,
            and its team shall not be liable for any direct, indirect, incidental, special,
            consequential, or punitive damages arising from:
          </p>
          <ul>
            <li>Your use of, or inability to use, the Free Site Audit Tool or any other part of this website.</li>
            <li>Any errors, inaccuracies, or omissions in audit results or AI-generated content.</li>
            <li>Any actions you take or do not take based on audit results or recommendations.</li>
            <li>Any interruption, suspension, or termination of the service.</li>
            <li>Any third-party service failures, downtime, or inaccuracies.</li>
          </ul>
          <p>
            Your sole remedy for dissatisfaction with the website or its content is to
            stop using the website. The maximum liability of Rajesh R in any event shall
            not exceed the amount paid by you for the specific service in question,
            which in the case of the free tool is zero (₹0).
          </p>
        </>
      ),
    },
    {
      num: 6,
      title: 'Intellectual Property',
      content: (
        <>
          <p>
            All content on this website — including text, graphics, logos, service names
            (GEO, AEO, LLMO, AISO, etc. as used in the context of our services), tool designs,
            and methodology — is the intellectual property of Rajesh R and is protected under
            applicable Indian and international intellectual property laws.
          </p>
          <p>
            You may not reproduce, distribute, modify, or create derivative works of any
            content from this website without prior written permission from Rajesh R.
          </p>
        </>
      ),
    },
    {
      num: 7,
      title: 'User Responsibilities',
      content: (
        <>
          <p>By using this website and its tools, you agree to:</p>
          <ul>
            <li>Submit only URLs of websites that you own or have explicit permission to audit.</li>
            <li>Not use the tool to audit websites for malicious or competitive intelligence purposes that violate any laws.</li>
            <li>Not attempt to reverse-engineer, misuse, or overload the tool or any third-party APIs.</li>
            <li>Keep your Gemini API key secure and not share it with unauthorised parties.</li>
            <li>Not rely solely on the tool's output for making critical business decisions.</li>
          </ul>
        </>
      ),
    },
    {
      num: 8,
      title: 'Paid Services & Consultations',
      content: (
        <>
          <p>
            Rajesh R offers paid AI optimisation and SEO consulting services separate from
            this free tool. Engaging paid services is subject to a separate service agreement
            or proposal that will be provided to you before any paid work begins.
          </p>
          <p>
            Any quotes, timelines, or outcomes discussed via WhatsApp, email, or phone are
            indicative only and not binding until confirmed in a written agreement signed
            by both parties.
          </p>
        </>
      ),
    },
    {
      num: 9,
      title: 'Changes to These Terms',
      content: (
        <p>
          We reserve the right to modify these Terms and Conditions at any time. Changes
          will be effective immediately upon posting to this page with an updated "Last
          Updated" date. Your continued use of this website after any change constitutes
          your acceptance of the new terms. We encourage you to review this page periodically.
        </p>
      ),
    },
    {
      num: 10,
      title: 'Governing Law & Jurisdiction',
      content: (
        <p>
          These Terms and Conditions are governed by and construed in accordance with
          the laws of India. Any disputes arising from your use of this website shall
          be subject to the exclusive jurisdiction of the courts located in Trivandrum
          (Thiruvananthapuram), Kerala, India.
        </p>
      ),
    },
  ];

  return (
    <>
      <Helmet>
        <title>Terms & Conditions – Free Site Audit Tool | Rajesh R</title>
        <meta name="description" content="Terms and Conditions for Rajesh R's website and Free Site Audit Tool. Understand accuracy limitations, disclaimer, and how to get professional AI optimization support." />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <div className="legal-page">

        {/* Hero */}
        <section className="legal-hero">
          <div className="container">
            <div className="legal-badge"><FileText size={12} /> Legal</div>
            <h1 className="legal-hero-title">Terms & Conditions</h1>
            <p className="legal-hero-sub">
              Please read these terms carefully before using this website or the Free Site Audit Tool.
            </p>
          </div>
        </section>

        {/* Body */}
        <section className="legal-body">
          <div className="container">
            <div className="legal-content">

              {/* Top disclaimer */}
              <div className="legal-disclaimer-box">
                <AlertTriangle size={20} color="#f59e0b" />
                <div>
                  <strong>Important Notice about the Free Site Audit Tool</strong>
                  <p>
                    The free audit tool provides automated, AI-assisted results that are
                    <strong> not guaranteed to be fully accurate</strong>. For a reliable,
                    expert-reviewed audit and optimisation strategy, please consult Rajesh R directly.
                  </p>
                </div>
              </div>

              {/* Sections */}
              {sections.map(sec => (
                <div key={sec.num} className="legal-section">
                  <div className="legal-section-header">
                    <span className="legal-section-num">{sec.num}</span>
                    <h2>{sec.title}</h2>
                  </div>
                  {sec.content}
                </div>
              ))}

              {/* Contact */}
              <div className="legal-contact-box">
                <h3>Questions about these Terms?</h3>
                <p>
                  If you have any questions about these Terms and Conditions or want to discuss
                  a professional AI optimisation engagement, contact us directly.
                </p>
                <div className="legal-contact-links">
                  <a href={WA_LINK} target="_blank" rel="noreferrer noopener" className="legal-contact-link primary">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    WhatsApp Us
                  </a>
                  <a href={`mailto:${CONTACT_EMAIL}`} className="legal-contact-link secondary">
                    <Mail size={14} /> {CONTACT_EMAIL}
                  </a>
                  <a href={`tel:${CONTACT_PHONE.replace(/\s/g,'')}`} className="legal-contact-link secondary">
                    <Phone size={14} /> {CONTACT_PHONE}
                  </a>
                </div>
              </div>

              <p className="legal-updated">Last Updated: {LAST_UPDATED} · {SITE_NAME} · <Link to="/privacy-policy" style={{color:'#8b5cf6'}}>Privacy Policy</Link></p>

            </div>
          </div>
        </section>

      </div>
    </>
  );
}
